import React, { useState, useEffect } from "react";
import CommentsForm from "components/common/CommentsForm";
import { BiHomeAlt } from "react-icons/bi";
import { GoPlus } from "react-icons/go";
import { FiLoader } from "react-icons/fi";
import { motion } from "framer-motion";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import TestimonialActionsDropdown from "components/dropdown/TestimonialDropdown";
import "styles/admin.css";
import TestimonialViewModal from "components/modal/TestimonialViewModal";

const API_BASE = "https://algoritm-api-edu.uz";

const api = axios.create({
  baseURL: API_BASE,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("accessToken");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const CommentsControl = ({ goBack }) => {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingTestimonial, setEditingTestimonial] = useState(null);
  const [viewOpen, setViewOpen] = useState(false);
  const [selectedTestimonial, setSelectedTestimonial] = useState(null);
  const [viewLoading, setViewLoading] = useState(false);

  const handleView = async (id) => {
    try {
      setViewLoading(true);
      setViewOpen(true);

      const res = await api.get(`/api/admin/Testimonial/GetById/${id}`);
      const t = res.data?.data || res.data?.item || res.data || {};

      if (!t.id) {
        toast.error("Fikr topilmadi");
        setViewOpen(false);
        return;
      }

      const BASE_URL = "https://algoritm-api-edu.uz/";

      setSelectedTestimonial({
        id: t.id,
        studentName: t.studentName || "",
        message: t.message || "",
        photoUrl: t.photoUrl ? `${BASE_URL}${t.photoUrl}` : null,
        videoUrl: t.videoUrl ? `${BASE_URL}${t.videoUrl}` : null,
        statusName: t.statusName || (t.canCancel ? "Confirmed" : "Created"),
      });
    } catch (err) {
      console.error("View error:", err);
      toast.error("Fikr ma'lumotlarini yuklab bo‘lmadi");
      setViewOpen(false);
    } finally {
      setViewLoading(false);
    }
  };

  const fetchTestimonials = async () => {
    try {
      setLoading(true);
      const res = await api.post("/api/admin/Testimonial/GetAll", {
        page: 1,
        pageSize: 100,
      });
      const list = res.data?.rows || res.data || [];
      setTestimonials(list);
      console.log(list);
    } catch (err) {
      console.error(err);
      toast.error("Fikr-mulohazalar yuklanmadi");
      setTestimonials([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTestimonials();
  }, []);

  const handleAdd = async (formData) => {
    const data = new FormData();
    data.append("StudentName", formData.name.trim());
    data.append("Message", formData.comment.trim());

    if (!formData.image) {
      toast.error("Rasm yuklash majburiy!");
      return;
    }
    data.append("Photo", formData.image);
    if (formData.video) data.append("Video", formData.video);

    try {
      await api.post("/api/admin/Testimonial/Add", data);
      toast.success("Fikr muvaffaqiyatli qo‘shildi!");
      setShowForm(false);
      fetchTestimonials();
    } catch (err) {
      const msg = err.response?.data?.errors
        ? Object.values(err.response.data.errors).flat().join(", ")
        : err.response?.data?.message || "Xato yuz berdi";
      toast.error("Xato: " + msg);
    }
  };

  const handleEdit = async (id) => {
    try {
      const res = await api.get(`/api/admin/Testimonial/GetById/${id}`);
      const t = res.data?.data || res.data?.item || res.data;

      if (!t?.id) {
        toast.error("Fikr topilmadi");
        return;
      }

      setEditingTestimonial({
        id: t.id,
        name: t.studentName || "",
        comment: t.message || "",
        imageUrl: t.photoUrl ? `${API_BASE}/${t.photoUrl}` : null,
        videoUrl: t.videoUrl ? `${API_BASE}/${t.videoUrl}` : null,
        statusName: t.statusName || "Created",
      });
      setShowForm(true);
    } catch (err) {
      toast.error("Ma'lumot yuklanmadi");
    }
  };

  const handleUpdate = async (formData) => {
    if (!editingTestimonial?.id) return;

    const data = new FormData();
    data.append("StudentName", formData.name.trim());
    data.append("Message", formData.comment.trim());

    if (formData.image && formData.image instanceof File) {
      data.append("Photo", formData.image);
    }
    if (formData.video && formData.video instanceof File) {
      data.append("Video", formData.video);
    }

    try {
      await api.put(
        `/api/admin/Testimonial/Update/${editingTestimonial.id}`,
        data
      );
      toast.success("Fikr yangilandi!");
      setShowForm(false);
      setEditingTestimonial(null);
      fetchTestimonials();
    } catch (err) {
      toast.error("Yangilashda xato");
    }
  };

  const handleSubmit = (formData) => {
    editingTestimonial ? handleUpdate(formData) : handleAdd(formData);
  };

  return (
    <>
      <ToastContainer position="top-right" autoClose={4000} theme="colored" />

      <div className="banner-page-wrapper">
        <motion.div
          className="banner-header"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <motion.div className="breadcrumb" whileHover={{ scale: 1.02 }}>
            <motion.div
              className="home-icon"
              whileHover={{ rotate: 360 }}
              onClick={goBack}
            >
              <BiHomeAlt />
            </motion.div>
            <span>/ Fikr-mulohazalar</span>
          </motion.div>

          <motion.button
            className="add-btn"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => {
              setEditingTestimonial(null);
              setShowForm(true);
            }}
          >
            <GoPlus />
            Fikr qo‘shish
          </motion.button>
        </motion.div>

        {showForm ? (
          <CommentsForm
            initialData={editingTestimonial}
            onSubmit={handleSubmit}
            onCancel={() => {
              setShowForm(false);
              setEditingTestimonial(null);
            }}
            requireImage={!editingTestimonial}
          />
        ) : loading ? (
          <div style={{ textAlign: "center", padding: "100px" }}>
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
            >
              <FiLoader size={50} />
            </motion.div>
            <p>Yuklanmoqda...</p>
          </div>
        ) : testimonials.length === 0 ? (
          <div style={{ textAlign: "center", padding: "120px", color: "#666" }}>
            <h2>Hozircha fikr yo‘q</h2>
            <p>"Fikr qo‘shish" tugmasini bosing</p>
          </div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <motion.table className="banner-table">
              <thead>
                <tr>
                  <th>№</th>
                  <th>O‘quvchi</th>
                  <th>Rasm</th>
                  <th>Izoh</th>
                  <th>Video</th>
                  <th>Status</th>
                  <th>Amallar</th>
                </tr>
              </thead>
              <tbody>
                {testimonials.map((t, i) => (
                  <motion.tr
                    key={t.id}
                    layout
                    initial={{ opacity: 0, x: -50 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.05 }}
                  >
                    <td>{i + 1}</td>
                    <td>
                      {t.photoUrl ? (
                        <img
                          src={`${API_BASE}/${t.photoUrl}`}
                          alt="student"
                          style={{
                            width: 60,
                            height: 60,
                            borderRadius: 8,
                            objectFit: "cover",
                          }}
                        />
                      ) : (
                        "—"
                      )}
                    </td>
                    <td>{t.studentName}</td>

                    <td style={{ maxWidth: 300 }}>
                      {t.message?.slice(0, 80)}...
                    </td>
                    <td>{t.videoUrl ? "Bor" : "—"}</td>
                    <td>
                      <span
                        className={`status-badge ${
                          t.canCancel ? "active" : "pending"
                        }`}
                        style={{
                          padding: "6px 12px",
                          borderRadius: "20px",
                          fontSize: "13px",
                          fontWeight: "600",
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                        }}
                      >
                        {t.canCancel ? "Faol" : "Kutilyapti"}
                      </span>
                    </td>
                    <td>
                      <TestimonialActionsDropdown
                        testimonial={t}
                        onEdit={() => handleEdit(t.id)}
                        refreshList={fetchTestimonials}
                        onView={() => handleView(t.id)}
                      />
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </motion.table>
          </motion.div>
        )}
      </div>
      <TestimonialViewModal
        isOpen={viewOpen}
        onClose={() => {
          setViewOpen(false);
          setSelectedTestimonial(null);
        }}
        testimonial={selectedTestimonial}
        loading={viewLoading}
      />
    </>
  );
};

export default CommentsControl;
